import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';

import useGetKeywordSearchHospitalInfinite from '@/hooks/search/hospital/useGetKeywordSearchHospitalInfinite';
import useCustomRouter from '@/hooks/useCustomRouter';
import useNavigate from '@/hooks/useNavigate';
import { ROUTES } from '@/types/common';
import { SearchHospitalVo } from '@/types/hospital';
import { EIntegratedSearchTabType, ERequestButtonType } from '@/types/search';
import { coreLogger } from '@/utils/logger';
import { isWebView } from '@/utils/mobile/RNPlugins';

interface Props {
  keyword: string;
  tab: EIntegratedSearchTabType;
  latitude: number;
  longitude: number;
}

// 병원찾기에서 SearchHospitalViewModel 사용중이라 앞에 Total 붙임
export interface TotalSearchHospitalViewModel {
  hospitalData: InfiniteData<SearchHospitalVo> | undefined;
  isHospitalLoading: boolean;
  hasHospitalNextPage: boolean | undefined;
  fetchHospitalNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult>;
  handleClickRequestButton: () => void;
  onClickMoveNative: (hospitalPid: string) => void;
  onClickLogHospital: (hospitalPid: string) => void;
}

const useTotalSearchHospitalViewModel = ({
  keyword,
  tab,
  latitude,
  longitude,
}: Props): TotalSearchHospitalViewModel => {
  const { pageName, moveToPage } = useCustomRouter();
  const navigate = useNavigate();
  const { hospitalData, isHospitalLoading, hasHospitalNextPage, fetchHospitalNextPage } =
    useGetKeywordSearchHospitalInfinite({
      keyword,
      enabled: tab === EIntegratedSearchTabType.Hospital,
      latitude,
      longitude,
    });

  const handleClickRequestButton = () => {
    coreLogger({
      eventName: 'request_additional_info_click',
      pageName: pageName,
      payload: {
        position: ERequestButtonType.BOTTOM,
        keyword,
        section_name: 'integrate_search_hospital',
      },
    });
    moveToPage({
      webLink: `${ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.path}`,
      mobileScreenName: ROUTES.SEARCH(keyword.replaceAll(' ', '+')).REQUEST_INFO.appScreenName,
      params: { keyword },
    });
  };

  const onClickMoveNative = (hospitalPid: string) => {
    if (isWebView()) {
      navigate.navigateToHospitalDetail({ hospitalPid });
    }
  };

  const onClickLogHospital = (hospitalPid: string) => {
    coreLogger({
      eventName: 'search_item_click',
      pageName: pageName,
      payload: {
        keyword,
        content_id: hospitalPid,
        tab: EIntegratedSearchTabType.Hospital,
      },
    });
  };

  return {
    hospitalData,
    isHospitalLoading,
    hasHospitalNextPage,
    fetchHospitalNextPage,
    handleClickRequestButton,
    onClickMoveNative,
    onClickLogHospital,
  };
};

export default useTotalSearchHospitalViewModel;
